import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@env';
import { StoreService } from '@core/services/store.service';
import { firstValueFrom, race, timer, filter, mapTo } from 'rxjs';
import { SessionEvent } from '@core/services/io.service';

import { DeviceTypeService } from './device-type.service';

export enum LogSeverity {
  DEFAULT = 'DEFAULT',
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  NOTICE = 'NOTICE',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  CRITICAL = 'CRITICAL',
  ALERT = 'ALERT',
  EMERGENCY = 'EMERGENCY',
}

@Injectable({ providedIn: 'root' })
export class LoggingService {
  private readonly writeLogUrl = `${environment.firebaseFunctionsUrl}/writeLog`;

  private storeId = '';
  private laneId = '';
  private sessionId = '';
  private socketId = '';

  // Keep package info for parity with both versions

  private readonly packageJson = require('../../../../package.json');
  private readonly buildVersion = this.packageJson.version;
  private readonly appName = this.packageJson.name;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private storeSvc: StoreService,
    private deviceTypeService: DeviceTypeService,
  ) {
    // Keep latest storeId on hand for goodbye/shutdown events
    this.storeSvc.storeId$.pipe(filter((id): id is string => !!id)).subscribe((id) => (this.storeId = id));
  }

  /** Waits up to 3 s for a non-empty storeId, otherwise returns `"unknown"` */
  private getStoreId(): Promise<string> {
    return firstValueFrom(
      race(this.storeSvc.storeId$.pipe(filter((id): id is string => !!id)), timer(3_000).pipe(mapTo('unknown'))),
    );
  }

  /* ------------------------------------------------------------------ *
   *  Public API (superset of CORE + SHARED)                             *
   * ------------------------------------------------------------------ */

  async writeLog(message: string, optionalParams: Record<string, unknown> = {}): Promise<void> {
    const storeId = await this.getStoreId();
    // Preserve SHARED behavior: prefer route query param for sessionId if present
    const sessionId = this.route.snapshot.queryParamMap.get('sessionId') ?? this.sessionId;
    const deviceType = this.deviceTypeService.getDeviceType();

    const base = {
      metadata: {
        application: this.appName,
        environment: environment.name,
        version: this.buildVersion,
        host: window.location.hostname,
        userAgent: window.navigator.userAgent,
        deviceType,
      },
      session: {
        laneId: this.laneId,
        sessionId,
        correlationId: this.getCorrelationId(),
        url: environment.proxyUrl,
      },
      message,
      socketId: this.socketId,
      storeId,
      sessionId,
    };

    const log = Object.assign({}, base, optionalParams);
    const payload = JSON.stringify(log);

    /** Skip network traffic in local dev ------------------------------- */
    if (environment.name === 'local') {
      this.consoleLog(`[LoggingService] (console only):`, log);
      return;
    }

    /** Preferred: Beacon API (fire-and-forget) ------------------------------- */
    if (navigator.sendBeacon) {
      try {
        const ok = navigator.sendBeacon(this.writeLogUrl, new Blob([payload], { type: 'application/json' }));
        if (ok) {
          this.consoleLog('[LoggingService] (beacon):', log);
          return;
        }
      } catch (err) {
        this.consoleLog('[LoggingService] Beacon failed, trying fetch:', err);
      }
    }

    /** Fetch fallback — omit creds to dodge CORS creds requirement */
    if ('fetch' in window) {
      fetch(this.writeLogUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        credentials: 'omit',
      })
        .then(() => this.consoleLog('[LoggingService] (fetch):', log))
        .catch((err) => {
          this.consoleLog('[LoggingService] Fetch failed, trying XHR:', err);
          this.xhrFallback(log);
        });
      return;
    }

    /** Legacy XHR fallback (Angular HttpClient) ------------------ */
    this.xhrFallback(log);
  }

  private pickConsole(sev?: string): (...args: unknown[]) => void {
    switch (sev) {
      case 'EMERGENCY':
      case 'ALERT':
      case 'CRITICAL':
      case 'ERROR':
        // eslint-disable-next-line no-console
        return console.error.bind(console);
      case 'WARNING':
        // eslint-disable-next-line no-console
        return console.warn.bind(console);
      case 'NOTICE':
      case 'INFO':
        // eslint-disable-next-line no-console
        return console.info.bind(console);
      case 'DEBUG':
      case 'DEFAULT':
      default:
        // eslint-disable-next-line no-console
        return console.debug.bind(console);
    }
  }

  consoleLog(message: string, ...optional: unknown[]): void {
    if (environment.name === 'production') return;

    // Try to find a severity in the optional params
    let severity: string | undefined;
    for (const a of optional) {
      if (a && typeof a === 'object') {
        // direct severity on the payload: { severity: 'INFO', ... }
        if ('severity' in (a as any) && typeof (a as any).severity === 'string') {
          severity = (a as any).severity;
          break;
        }
        // nested detail (just in case): { detail: { severity: 'INFO' } }
        if ('detail' in (a as any) && (a as any).detail && typeof (a as any).detail.severity === 'string') {
          severity = (a as any).detail.severity;
          break;
        }
      }
      // If an Error is present and no explicit severity yet, treat as ERROR
      if (!severity && a instanceof Error) {
        severity = 'ERROR';
      }
    }

    const out = this.pickConsole(severity);

    out(message, ...optional);
  }

  // ---- Mutators kept for compatibility with legacy services ----
  setSession(e: SessionEvent): void {
    this.storeId = e.storeId;
    this.laneId = e.laneId;
    this.sessionId = e.sessionId;
  }
  setStoreId(id: string): void {
    this.storeId = id;
  }
  setSessionId(id: string): void {
    this.sessionId = id;
  }
  setSocketId(id: string): void {
    this.socketId = id;
  }

  /* ------------------------------------------------------------------ *
   *  Helpers                                                            *
   * ------------------------------------------------------------------ */
  private xhrFallback(log: unknown): void {
    this.http
      .post(this.writeLogUrl, log, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        withCredentials: false,
      })
      .subscribe({
        next: () => this.consoleLog('[LoggingService] (xhr):', log),
        error: (err) => this.consoleLog('[LoggingService] XHR logging failed:', err),
      });
  }

  /** Reads correlationId from localStorage (or returns undefined) */
  private getCorrelationId(): string | undefined {
    try {
      const val = localStorage.getItem('correlationId');
      return val ?? undefined;
    } catch {
      // LocalStorage may be unavailable in some contexts (SSR, privacy mode)
      return undefined;
    }
  }
}

/** Backward-compat class name (legacy /shared/services/logging.service export). */
export class LoggingServiceExternal extends LoggingService {}
